import { useEffect, useRef, useState } from "react";
import { Microphone, Stop, Trash, UploadSimple } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";

export default function AudioRecorder({ onFile, existingUrl, disabled }) {
    const { t } = useI18n();
    const [recording, setRecording] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(existingUrl || null);
    const [seconds, setSeconds] = useState(0);
    const mediaRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const streamRef = useRef(null);

    useEffect(() => setPreviewUrl(existingUrl || null), [existingUrl]);

    useEffect(() => () => {
        try { streamRef.current?.getTracks?.().forEach(t => t.stop()); } catch (_) { /* ignore */ }
        if (timerRef.current) clearInterval(timerRef.current);
    }, []);

    const start = async () => {
        if (disabled) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            // Prefer explicit codec so blob has proper container/codec headers for playback.
            const preferredMimes = [
                "audio/webm;codecs=opus",
                "audio/ogg;codecs=opus",
                "audio/webm",
                "audio/mp4;codecs=mp4a.40.2",
                "audio/mp4",
            ];
            const mime = preferredMimes.find((m) => {
                try { return MediaRecorder.isTypeSupported(m); } catch { return false; }
            }) || "";
            const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
            chunksRef.current = [];
            mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
            mr.onstop = () => {
                const finalType = mr.mimeType || mime || "audio/webm";
                const blob = new Blob(chunksRef.current, { type: finalType });
                const ext = finalType.includes("mp4") ? "m4a" : finalType.includes("ogg") ? "ogg" : "webm";
                const file = new File([blob], `voice-message.${ext}`, { type: finalType });
                setPreviewUrl(URL.createObjectURL(blob));
                onFile?.(file);
                streamRef.current?.getTracks?.().forEach(t => t.stop());
            };
            mr.start();
            mediaRef.current = mr;
            setRecording(true); setSeconds(0);
            timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
        } catch (e) {
            alert("Microphone access denied. You can upload an audio file instead.");
        }
    };

    const stop = () => {
        if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
        setRecording(false);
        if (timerRef.current) clearInterval(timerRef.current);
    };

    const clear = () => { setPreviewUrl(null); onFile?.(null); };
    const onPick = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setPreviewUrl(URL.createObjectURL(f));
        onFile?.(f);
    };

    return (
        <div className="rounded-2xl border border-paw-line dark:border-white/10 bg-paw-bone dark:bg-white/5 p-4">
            <div className="flex flex-wrap items-center gap-3">
                {!recording ? (
                    <button type="button" data-testid="audio-record-start"
                        onClick={start} disabled={disabled}
                        className="btn-primary !py-2 !px-4 text-sm disabled:opacity-50">
                        <Microphone size={16} weight="fill" />
                        <span className="ms-1.5">{t("common.record")}</span>
                    </button>
                ) : (
                    <button type="button" data-testid="audio-record-stop"
                        onClick={stop}
                        className="btn-secondary !py-2 !px-4 text-sm">
                        <Stop size={16} weight="fill" />
                        <span className="ms-1.5">{t("common.stop")} ({seconds}s)</span>
                    </button>
                )}

                <label className={`btn-outline !py-2 !px-4 text-sm cursor-pointer ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
                    <UploadSimple size={16} />
                    <span className="ms-1.5">{t("common.upload")}</span>
                    <input data-testid="audio-file-input" type="file" accept="audio/*" className="hidden" onChange={onPick} />
                </label>

                {previewUrl && (
                    <button type="button" onClick={clear} data-testid="audio-clear"
                        className="btn-ghost !py-2 !px-3 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20">
                        <Trash size={16} />
                        <span className="ms-1.5">{t("common.remove")}</span>
                    </button>
                )}
            </div>
            {previewUrl && (
                <audio data-testid="audio-preview" src={previewUrl} controls className="mt-3 w-full" />
            )}
        </div>
    );
}
